/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/FuelDensities/AdminFuelDensities.tsx.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../../../components/common/Button";
import OutlineButton from "../../../../components/common/OutlineButton";
import {
  fuelDensitiesAdminService,
  type FuelDensityAdmin,
} from "../../../../services/fuelDensitiesAdminService";
import { useFuelMixDropdownOptions } from "../shared/useFuelMixDropdownOptions";
import "../Admin.css";
import "../CharacterizationFactors/AdminCharacterizationFactors.css";

const emptyForm = () => ({
  fuelName: "",
  densityKgM3: "",
  densityKgL: "",
  sortOrder: "0",
  isActive: true,
});

function parseDensityInput(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const normalizeFuelName = (s: string) => s.trim().toLowerCase();

const AdminFuelDensities: React.FC = () => {
  const [rows, setRows] = useState<FuelDensityAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const [deleteTarget, setDeleteTarget] = useState<FuelDensityAdmin | null>(
    null
  );

  const { options: fuelMixOptions } = useFuelMixDropdownOptions();

  /** Fuel names already used by other rows (lower-cased). Used to block duplicates. */
  const takenFuelNames = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (editingId != null && r.id === editingId) continue;
      const k = normalizeFuelName(r.fuelName ?? "");
      if (k) set.add(k);
    }
    return set;
  }, [rows, editingId]);

  /** Dropdown options minus fuels already used by other rows. */
  const availableFuelMixOptions = useMemo(
    () =>
      fuelMixOptions.filter(
        (o) => !takenFuelNames.has(normalizeFuelName(o.label))
      ),
    [fuelMixOptions, takenFuelNames]
  );

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await fuelDensitiesAdminService.list();
      setRows(data);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Failed to load fuel densities"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const o = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        return o !== 0 ? o : a.id - b.id;
      }),
    [rows]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (r: FuelDensityAdmin) => {
    setEditingId(r.id);
    setForm({
      fuelName: r.fuelName ?? "",
      densityKgM3: String(r.densityKgM3 ?? ""),
      densityKgL: String(r.densityKgL ?? ""),
      sortOrder: String(r.sortOrder ?? 0),
      isActive: Boolean(r.isActive),
    });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleSave = async () => {
    const fuelName = form.fuelName.trim();
    const dM3 = parseDensityInput(form.densityKgM3);
    const dL = parseDensityInput(form.densityKgL);

    if (!fuelName) {
      setError("Fuel name is required.");
      return;
    }
    if (takenFuelNames.has(normalizeFuelName(fuelName))) {
      setError(
        `A density entry for "${fuelName}" already exists. Edit that row instead of adding a new one.`
      );
      return;
    }
    if (dM3 == null) {
      setError("Density (kg/m³) is required and must be a valid number.");
      return;
    }
    if (dL == null) {
      setError("Density (kg/L) is required and must be a valid number.");
      return;
    }

    const sortOrder = Number.parseInt(String(form.sortOrder).trim(), 10);
    const sortOrderNum = Number.isFinite(sortOrder) ? sortOrder : 0;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingId == null) {
        await fuelDensitiesAdminService.create({
          fuelName,
          densityKgM3: dM3,
          densityKgL: dL,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        });
        setSuccess("Fuel density created.");
      } else {
        await fuelDensitiesAdminService.update(editingId, {
          fuelName,
          densityKgM3: dM3,
          densityKgL: dL,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        });
        setSuccess("Fuel density updated.");
      }
      closeModal();
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await fuelDensitiesAdminService.delete(deleteTarget.id);
      setSuccess("Deleted.");
      setDeleteTarget(null);
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="background-form-container analysis-shell admin-char-factors">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Fuel densities</span>
      </div>

      <div className="admin-form-root">
        <div className="dataset-container">
          <div className="dataset-section">
            <h3 className="dataset-section-title">Manage reference fuel densities</h3>
            <p className="muted" style={{ marginBottom: "1rem" }}>
              Reference kg/m³ and kg/L values used when contributors pick a fuel on
              Form A1. Inactive rows are omitted from the public list (
              <code>GET /fuel-densities</code>). Admin list includes all rows.
            </p>

            {error && !modalOpen && !deleteTarget && (
              <div className="error-message" style={{ marginBottom: 12 }}>
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="success-message" style={{ marginBottom: 12 }}>
                <span className="success-icon">✅</span>
                <span>{success}</span>
              </div>
            )}

            <div className="char-factors-toolbar">
              <Button
                label="Add row"
                type="button"
                onClick={openCreate}
                disabled={loading}
              />
            </div>

            {loading ? (
              <p>Loading…</p>
            ) : sortedRows.length === 0 ? (
              <p className="muted">No rows yet. Click &quot;Add row&quot; to create one.</p>
            ) : (
              <div className="char-factors-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Fuel name</th>
                      <th>kg/m³</th>
                      <th>kg/L</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th className="cell-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.sortOrder}</td>
                        <td>{r.fuelName}</td>
                        <td>{r.densityKgM3}</td>
                        <td>{r.densityKgL}</td>
                        <td>
                          {r.isActive ? (
                            <span>Active</span>
                          ) : (
                            <span className="badge-inactive">Inactive</span>
                          )}
                        </td>
                        <td className="muted" style={{ fontSize: "0.8rem" }}>
                          {r.updatedAt
                            ? new Date(r.updatedAt).toLocaleString()
                            : "—"}
                        </td>
                        <td className="cell-actions">
                          <OutlineButton
                            label="Edit"
                            type="button"
                            onClick={() => openEdit(r)}
                          />
                          <OutlineButton
                            label="Delete"
                            type="button"
                            onClick={() => {
                              setError(null);
                              setDeleteTarget(r);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className="modal-panel"
            role="dialog"
            aria-labelledby="fd-modal-title"
          >
            <div className="modal-header" id="fd-modal-title">
              {editingId == null ? "New fuel density" : "Edit fuel density"}
            </div>
            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label htmlFor="fd-fuelName">Fuel name *</label>
                <select
                  id="fd-fuelName"
                  value={
                    availableFuelMixOptions.some(
                      (o) => o.label === form.fuelName
                    )
                      ? form.fuelName
                      : ""
                  }
                  onChange={(e) =>
                    setForm((f) => ({ ...f, fuelName: e.target.value }))
                  }
                  disabled={saving}
                >
                  <option value="">Select a fuel mix…</option>
                  {availableFuelMixOptions.map((o) => (
                    <option key={o.id} value={o.label}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {fuelMixOptions.length === 0 ? (
                  <p className="muted">
                    No “Fuel Mix” dropdown options found. Add options in
                    “Manage dropdowns”.
                  </p>
                ) : availableFuelMixOptions.length === 0 ? (
                  <p className="muted">
                    Every fuel in the “Fuel Mix” dropdown already has a density
                    entry. Edit an existing row instead.
                  </p>
                ) : (
                  <p className="muted">
                    Each fuel can only have one density entry. Fuels already
                    used in other rows are hidden.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="fd-kgm3">Density kg/m³ *</label>
                <input
                  id="fd-kgm3"
                  type="text"
                  inputMode="decimal"
                  value={form.densityKgM3}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, densityKgM3: e.target.value }))
                  }
                  disabled={saving}
                  placeholder="e.g. 832"
                />
              </div>
              <div>
                <label htmlFor="fd-kgl">Density kg/L *</label>
                <input
                  id="fd-kgl"
                  type="text"
                  inputMode="decimal"
                  value={form.densityKgL}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, densityKgL: e.target.value }))
                  }
                  disabled={saving}
                  placeholder="e.g. 0.832"
                />
              </div>
              <div>
                <label htmlFor="fd-sort">Sort order</label>
                <input
                  id="fd-sort"
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sortOrder: e.target.value }))
                  }
                  disabled={saving}
                />
                <p className="muted">Lower numbers appear first in public lists.</p>
              </div>
              <div className="checkbox-row">
                <input
                  id="fd-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                  disabled={saving}
                />
                <label htmlFor="fd-active">Active (visible on public API)</label>
              </div>
            </div>
            <div className="modal-footer">
              <OutlineButton
                label="Cancel"
                type="button"
                onClick={closeModal}
                disabled={saving}
              />
              <Button
                label={saving ? "Saving…" : "Save"}
                type="button"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setDeleteTarget(null);
          }}
        >
          <div
            className="modal-panel"
            role="alertdialog"
            aria-labelledby="fd-del-title"
          >
            <div className="modal-header" id="fd-del-title">
              Delete fuel density?
            </div>
            <div className="modal-body">
              <p>
                Remove <strong>{deleteTarget.fuelName}</strong> (
                {deleteTarget.densityKgL} kg/L)?
              </p>
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <OutlineButton
                label="Cancel"
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={saving}
              />
              <Button
                label={saving ? "Deleting…" : "Delete"}
                type="button"
                onClick={confirmDelete}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** React component — Admin Fuel Densities. */
export default AdminFuelDensities;
